import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { CTA_SOLID, CtaStyles, ctaOverLight } from './_cta';

// ─── Slot group definitions ───────────────────────────────────────────────────

const imageDef: SlotGroupDefinition = {
  label: 'Neighborhood Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'property_featured_image_media.alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select one photo of the building exterior or facade — a street-level or storefront view of the property that conveys its neighborhood setting. Choose a different exterior shot than the one used in the property overview section, and avoid interior apartment shots.',
    context: 'Single featured exterior image beside the neighborhood copy on the home page.',
    sourceCollection: 'photo_gallery',
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Neighborhood CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Label', type: 'text', defaultValue: 'Discover Our Neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function PinnedCallouts() {
  const image = useSlotGroup('pinnedcallouts.image', imageDef);
  const ctaLink = useSlotGroup('pinnedcallouts.cta_link', ctaLinkDef);

  useRevealStyles();
  const containerRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-12 md:py-20 bg-[var(--vibe-color-background)] overflow-hidden">
      <CtaStyles />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={containerRef}
          className="reveal-fade-up grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center"
        >
          {/* Arched image */}
          <div className="neighborhood-arch-frame mx-auto w-full max-w-md">
            <EditableSlotGroup groupPath="pinnedcallouts.image" definition={imageDef}>
              <div className="neighborhood-arch-clip aspect-[8/9]">
                {image.image ? (
                  <VibesitesImage
                    url={image.image}
                    alt={image.alt || ''}
                    sizes="(min-width: 768px) 32rem, 100vw"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[color-mix(in_srgb,var(--vibe-color-primary)_20%,var(--vibe-color-background))]" />
                )}
              </div>
            </EditableSlotGroup>
          </div>

          {/* Copy */}
          <div className="text-center md:text-left">
            <RichTextContent
              path="pinnedcallouts.title"
              as="h2"
              className="font-heading text-[clamp(1.75rem,6vw,2.25rem)] sm:text-5xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-[var(--vibe-color-primary)] mb-6 leading-[1.1] sm:leading-[1.05]"
            />

            <RichTextContent
              path="pinnedcallouts.description"
              as="div"
              className="font-body text-base sm:text-lg text-[var(--vibe-color-text)]/75 max-w-xl leading-relaxed [&_a]:text-primary [&_a]:underline"
            />

            <EditableSlotGroup groupPath="pinnedcallouts.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/neighborhood'}
                target={ctaLink._target || '_self'}
                className={`mt-10 neighborhood-cta ${CTA_SOLID}`}
                style={ctaOverLight}
              >
                {ctaLink.label || 'Discover Our Neighborhood'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* CTA size is inherited from the canonical .v-cta (see _cta.tsx), shared
           by every CTA in the theme. */
        /* On hover, fill background + border with the resolved secondary hover
           fill (the --cta-hover-fill variable, published by CtaStyles and
           auto-darkened when a white label wouldn't clear WCAG AA on the raw
           secondary) and keep the label white. Higher specificity than the shared
           .v-cta--solid:hover, and the ::before darkening overlay is suppressed
           so the fill shows. */
        .neighborhood-cta.v-cta--solid:hover {
          color: #ffffff;
          background: var(--cta-hover-fill, var(--vibe-color-secondary));
          border-color: var(--cta-hover-fill, var(--vibe-color-secondary));
        }
        .neighborhood-cta.v-cta--solid:hover::before {
          opacity: 0;
        }
        .neighborhood-arch-frame {
          border: 2px solid var(--vibe-color-primary);
          border-radius: 50% 50% 6px 6px / 42% 42% 6px 6px;
          padding: 14px;
        }
        .neighborhood-arch-clip {
          border-radius: 50% 50% 3px 3px / 40% 40% 3px 3px;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PinnedCallouts',
  displayName: 'Neighborhood',
  tokens: {
    'pinnedcallouts.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>A day in the life at <span data-type="field-token" data-field="property_name"></span>’s neighborhood.</p>',
    },
    'pinnedcallouts.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      defaultValue:
        '<p>Ideally located near the best of the city, our community is surrounded by a variety of entertainment, dining, and essential services. With convenient access to local favorites, top-rated schools, and shopping centers nearby, everything you need is just moments away. Discover your new home and experience the best of city living.</p>',
    },
  },
  slotGroups: {
    'pinnedcallouts.image': imageDef,
    'pinnedcallouts.cta_link': ctaLinkDef,
  },
};

(PinnedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PinnedCallouts;
